<script setup lang="ts">
import type { InputNumberEmits, InputNumberSlots, InputNumberProps as UInputNumberProps } from '#ui/types';
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed, useTemplateRef } from 'vue';
import InputNumber from '#ui/components/InputNumber.vue';

export type InputNumberProps<T extends number | null = number | null> = UInputNumberProps<T>;

const props = withDefaults(defineProps<InputNumberProps>(), {
	orientation: 'vertical',
	// Both are typed `boolean | ButtonProps`, so an absent one casts to `false` and reads as an
	// explicit opt-out. Restate the upstream default to keep the steppers on unless asked.
	increment: true,
	decrement: true,
});
const emits = defineEmits<InputNumberEmits>();
const slots = defineSlots<InputNumberSlots>();

const forward = useForwardPropsEmits(reactiveOmit(props, 'decrement', 'increment'), emits);

// A read-only field drops the steppers rather than showing a control that refuses to act; that
// also releases the padding the theme reserves for them.
function resolveStepper(stepper: InputNumberProps['increment']) {
	if (props.readonly || stepper === false) return false;
	return Object.assign({}, typeof stepper === 'object' ? stepper : undefined, { color: 'neutral', class: 'text-dimmed' } as const);
}

const increment = computed(() => resolveStepper(props.increment));
const decrement = computed(() => resolveStepper(props.decrement));

const inputRef = useTemplateRef('input');

defineExpose({
	inputRef: computed(() => inputRef.value?.inputRef),
});
</script>

<template>
	<InputNumber
		ref="input"
		v-bind="forward"
		:decrement
		:increment
	>
		<template v-for="(_, slotName) in slots" #[slotName]="slotData: any">
			<slot :name="slotName" v-bind="slotData" />
		</template>
	</InputNumber>
</template>
